import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/brand-logo";
import { useLanguage } from "@/components/language-provider";
import { API_BASE_URL } from "@/lib/config";

interface AuthCallbackProps {
  provider?: "google" | "microsoft";
}

export default function AuthCallback({ provider: propProvider }: AuthCallbackProps) {
  const { t } = useLanguage();
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("Authenticating...");

  // Detect provider from URL if not passed as prop
  const provider = propProvider || (window.location.pathname.includes("google") ? "google" : "microsoft");

  useEffect(() => {
    const processAuth = async () => {
      try {
        // Parse hash fragment (Google returns tokens in hash)
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        
        console.log("Hash params:", Object.fromEntries(hashParams.entries()));
        
        // Google implicit flow returns id_token in the hash
        const idToken = hashParams.get("id_token");
        const accessToken = hashParams.get("access_token");
        const errorParam = hashParams.get("error");
        
        if (errorParam) {
          throw new Error(hashParams.get("error_description") || errorParam);
        }
        
        // We need the id_token for authentication
        if (!idToken) {
          console.error("No id_token received. Got access_token:", !!accessToken);
          throw new Error("No ID token received from Google. Please try again.");
        }

        setStatus(t("auth.verifying"));
        console.log("Sending id_token to backend...");
        
        const endpoint = provider === "google" 
          ? "/api/auth/oauth/google" 
          : "/api/auth/oauth/microsoft";
        
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        });
        
        const text = await res.text();
        console.log("Backend response:", text.substring(0, 500));
        
        let data;
        try {
          data = text ? JSON.parse(text) : {};
        } catch {
          throw new Error("Invalid server response");
        }
        
        if (!res.ok) {
          throw new Error(data.errors?.[0]?.message || data.message || "Authentication failed");
        }
        
        // Handle wrapped response
        const responseData = data.data || data;
        
        // Check if this is a new user who needs to complete registration
        if (responseData.requiresRegistrationCompletion) {
          console.log("New OAuth user - redirecting to complete registration");
          setStatus(t("auth.newAccount"));
          
          // Store OAuth data temporarily for registration completion
          sessionStorage.setItem("oauth_pending", JSON.stringify({
            provider,
            email: responseData.email,
            firstName: responseData.firstName,
            lastName: responseData.lastName,
            providerId: responseData.providerId,
            idToken: idToken,
          }));
          
          // Redirect to complete registration
          setTimeout(() => setLocation("/complete-registration"), 500);
          return;
        }
        
        // Existing user - check for token and user
        const token = responseData.token || responseData.Token;
        const user = responseData.user || responseData.User;
        
        if (!token || !user) {
          console.error("Response data:", responseData);
          throw new Error("Invalid response from server - missing token or user");
        }
        
        setStatus(t("auth.success"));
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
        
        // Clear any registration flags
        sessionStorage.removeItem("oauth_register");
        sessionStorage.removeItem("oauth_nonce");
        
        setTimeout(() => setLocation("/dashboard"), 500);
        
      } catch (err: any) {
        console.error("OAuth error:", err);
        setError(err.message || t("auth.failedTitle"));
        setTimeout(() => setLocation("/login"), 3000);
      }
    };

    processAuth();
  }, [provider, setLocation]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <BrandLogo />
          </div>
          <CardTitle>
            {error ? t("auth.failedTitle") : t("auth.signingIn")}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {error ? (
            <p className="text-destructive text-center">{error}</p>
          ) : (
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          )}
          <p className="text-sm text-muted-foreground">
            {error ? t("errors.redirectingLogin") : status}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
